import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ApiError } from '@/api/client';
import { SpriteIcon } from '@/components/SpriteIcon';
import { Avatar } from '@/layout/Avatar';
import { useDisableMfa, useMfaStatus, useRegenerateRecoveryCodes } from '@/pages/auth/useMfa';
import { RecoveryCodeList } from '@/pages/auth/RecoveryCodeList';
import {
  useProfile,
  useUpdatePreferences,
  useUpdateProfile,
  type UpdatePreferencesRequest,
  type UpdateProfileRequest,
} from './useProfile';

/**
 * Replaces `Pages/User/Profile.cshtml` at `/user-profile`.
 *
 * Two independent forms, as the Razor page had two independent posts: identity
 * details and preferences save separately, so changing your language does not
 * require re-submitting your phone number.
 *
 * The theme selector writes a stored preference; it is not the live theme
 * switch. `LayoutProvider` owns what the browser is currently rendering and
 * keeps it in localStorage — see the note on the theme field below.
 */

function errorText(error: unknown): string | null {
  if (!(error instanceof ApiError)) return null;
  const problem = error.problem as { errors?: string[] } | null;
  return Array.isArray(problem?.errors) && problem.errors.length > 0
    ? problem.errors.join(' ')
    : error.message;
}

function formatDateTime(value: string | null): string {
  if (!value) return '—';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? '—' : parsed.toLocaleString();
}

/**
 * Multi-factor state and the controls to change it.
 *
 * Enrolling happens on `/auth-mfa-setup`, the same screen the guard forces a
 * mandated user onto — one implementation for both routes in. Only disabling
 * lives here, because it needs the password and has nowhere else to go.
 *
 * The whole card disappears when the feature is switched off application-wide,
 * and the Disable button disappears when the user's role mandates MFA. Neither
 * is the security boundary: the server refuses both independently.
 */
function SecurityCard() {
  const { t } = useTranslation();
  const { data: mfa, isLoading } = useMfaStatus();
  const disableMfa = useDisableMfa();
  const regenerateCodes = useRegenerateRecoveryCodes();

  // Disabling and regenerating both need the password, so they share one prompt
  // and differ only in what confirming it does.
  const [confirming, setConfirming] = useState<null | 'disable' | 'regenerate'>(null);
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [freshCodes, setFreshCodes] = useState<string[] | null>(null);

  if (isLoading || !mfa || !mfa.featureEnabled) return null;

  const dismissPrompt = () => {
    setConfirming(null);
    setPassword('');
    setError(null);
  };

  const onConfirm = async () => {
    setError(null);

    try {
      if (confirming === 'disable') {
        await disableMfa.mutateAsync(password);
        setFreshCodes(null);
      } else {
        const result = await regenerateCodes.mutateAsync(password);
        setFreshCodes(result.twoFactor.recoveryCodes ?? []);
      }

      setConfirming(null);
      setPassword('');
    } catch (err) {
      setError(
        errorText(err) ??
          (confirming === 'disable'
            ? t('Profile.Mfa.DisableFailed', 'Could not turn it off.')
            : t('Profile.Mfa.RegenerateFailed', 'Could not generate new codes.')),
      );
    }
  };

  const pending = disableMfa.isPending || regenerateCodes.isPending;
  const runningLow = mfa.isTwoFactorEnabled && mfa.recoveryCodesLeft <= 3;

  return (
    <div className="card shadow-sm mt-3">
      <div className="card-header bg-transparent">
        <h5 className="mb-0">{t('Profile.Security', 'Security')}</h5>
      </div>
      <div className="card-body">
        <dl className="row mb-3 fs-sm">
          <dt className="col-6">{t('Profile.TwoFactor', 'Two-step verification')}</dt>
          <dd className="col-6 text-end mb-0">
            {mfa.isTwoFactorEnabled ? (
              <span className="badge bg-success">{t('auth:Auth.MFA.Enabled', 'On')}</span>
            ) : (
              <span className="badge bg-secondary">{t('auth:Auth.MFA.Disabled', 'Off')}</span>
            )}
          </dd>

          {mfa.isTwoFactorEnabled && (
            <>
              <dt className="col-6">{t('Profile.RecoveryCodes', 'Recovery codes')}</dt>
              <dd className="col-6 text-end mb-0">
                <span className={runningLow ? 'text-danger fw-semibold' : undefined}>
                  {t('Profile.RecoveryCodesLeft', '{{count}} left', {
                    count: mfa.recoveryCodesLeft,
                  })}
                </span>
              </dd>
            </>
          )}
        </dl>

        {runningLow && (
          <div className="alert alert-warning small py-2 d-flex align-items-start" role="alert">
            <SpriteIcon name="alert-triangle" size={16} className="sa-icon me-2 flex-shrink-0" />
            <div>
              {mfa.recoveryCodesLeft === 0
                ? t(
                    'auth:Auth.MFA.NoCodesLeft',
                    'You have no recovery codes left. If you lose your authenticator you will be locked out.',
                  )
                : t(
                    'auth:Auth.MFA.LowCodes',
                    'You are running low on recovery codes. Generate a new set while you still can.',
                  )}
            </div>
          </div>
        )}

        {freshCodes && freshCodes.length > 0 && (
          <div className="mb-3">
            <RecoveryCodeList codes={freshCodes} />
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm w-100"
              onClick={() => setFreshCodes(null)}
            >
              {t('Done', 'Done')}
            </button>
          </div>
        )}

        {mfa.isMandatory && (
          <p className="text-muted small d-flex align-items-start">
            <SpriteIcon name="shield" size={16} className="sa-icon me-2 flex-shrink-0" />
            <span>
              {t(
                'auth:Auth.MFA.DisableMandatory',
                'Your role requires two-step verification, so it cannot be turned off.',
              )}
            </span>
          </p>
        )}

        <div className="d-grid gap-2">
          <Link to="/auth-mfa-setup" className="btn btn-outline-primary btn-sm">
            {mfa.isTwoFactorEnabled
              ? t('auth:Auth.MFA.Replace', 'Set up a new authenticator')
              : t('auth:Auth.MFA.Enable', 'Turn on two-step verification')}
          </Link>

          {mfa.isTwoFactorEnabled && confirming === null && (
            <button
              type="button"
              className={
                runningLow
                  ? 'btn btn-warning btn-sm d-flex align-items-center justify-content-center'
                  : 'btn btn-outline-secondary btn-sm d-flex align-items-center justify-content-center'
              }
              onClick={() => setConfirming('regenerate')}
            >
              <SpriteIcon name="key" size={14} className="sa-icon me-1 flex-shrink-0" />
              {t('auth:Auth.MFA.RegenerateCodes', 'Generate new recovery codes')}
            </button>
          )}

          {mfa.canDisable && confirming === null && (
            <button
              type="button"
              className="btn btn-outline-danger btn-sm"
              onClick={() => setConfirming('disable')}
            >
              {t('auth:Auth.MFA.Disable', 'Turn off')}
            </button>
          )}
        </div>

        {confirming !== null && (
          <div className="mt-3">
            {confirming === 'regenerate' && (
              <p className="text-muted small mb-2">
                {t(
                  'auth:Auth.MFA.RegenerateWarning',
                  'Generating a new set immediately invalidates every code you already have.',
                )}
              </p>
            )}
            <label className="form-label small" htmlFor="mfa-disable-password">
              {confirming === 'disable'
                ? t('auth:Auth.MFA.DisableConfirm', 'Enter your password to turn it off')
                : t('auth:Auth.MFA.RegenerateConfirm', 'Enter your password to generate new codes')}
            </label>
            <input
              id="mfa-disable-password"
              type="password"
              className="form-control form-control-sm mb-2"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            {error && <div className="text-danger small mb-2">{error}</div>}
            <div className="d-flex gap-2">
              <button
                type="button"
                className={
                  confirming === 'disable' ? 'btn btn-danger btn-sm' : 'btn btn-primary btn-sm'
                }
                disabled={pending || password.length === 0}
                onClick={() => void onConfirm()}
              >
                {t('Confirm', 'Confirm')}
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={dismissPrompt}
              >
                {t('Cancel', 'Cancel')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export function ProfilePage() {
  const { t } = useTranslation();
  const { data, isLoading, isError } = useProfile();

  const updateProfile = useUpdateProfile();
  const updatePreferences = useUpdatePreferences();

  const [details, setDetails] = useState<UpdateProfileRequest | null>(null);
  const [prefs, setPrefs] = useState<UpdatePreferencesRequest | null>(null);
  const [notice, setNotice] = useState<{ tone: 'success' | 'danger'; text: string } | null>(null);

  // Load the forms once the profile arrives. Keyed on the user id so a refetch
  // does not overwrite an edit in progress — the same trap the role-permissions
  // screen fell into.
  const loadedFor = useRef<string | null>(null);
  useEffect(() => {
    const profile = data?.profile;
    if (!profile || loadedFor.current === profile.userId) return;

    loadedFor.current = profile.userId;
    setDetails({
      firstName: profile.firstName,
      lastName: profile.lastName,
      phoneNumber: profile.phoneNumber,
      department: profile.department,
      jobTitle: profile.jobTitle,
    });
    setPrefs({
      preferredLanguage: profile.preferredLanguage,
      preferredTimezone: profile.preferredTimezone,
      preferredTheme: profile.preferredTheme,
    });
  }, [data]);

  if (isError) {
    return (
      <div className="alert alert-danger m-3" role="alert">
        {t('Profile.LoadError', 'Could not load your profile. Please try again.')}
      </div>
    );
  }

  if (isLoading || !data || !details || !prefs) {
    return (
      <div className="text-center py-5">
        <span className="spinner-border spinner-border-sm me-2" role="status" />
        {t('Loading', 'Loading...')}
      </div>
    );
  }

  const { profile, availableLanguages, availableThemes, availableTimezones } = data;

  return (
    <div className="container-fluid py-3">
      <div className="mb-3">
        <h1 className="h3 mb-1">{t('Profile.Title', 'My profile')}</h1>
        <p className="text-muted mb-0">
          {t('Profile.Subtitle', 'Your details, preferences and account status.')}
        </p>
      </div>

      {notice && (
        <div className={`alert alert-${notice.tone} alert-dismissible`} role="alert">
          {notice.text}
          <button type="button" className="btn-close" onClick={() => setNotice(null)} />
        </div>
      )}

      <div className="row g-3">
        <div className="col-lg-4">
          <div className="card shadow-sm mb-3">
            <div className="card-body text-center">
              <div className="d-flex justify-content-center mb-3">
                <Avatar user={profile} sizeClass="profile-image-lg" initialsClass="fw-bold fs-3" />
              </div>
              <h5 className="mb-1">{profile.displayName}</h5>
              <p className="text-muted mb-2">{profile.email}</p>
              <div className="mb-0">
                {profile.roles.map((role) => (
                  <span className="badge bg-primary me-1" key={role}>
                    {role}
                  </span>
                ))}
              </div>
            </div>
          </div>

          <div className="card shadow-sm">
            <div className="card-header bg-transparent">
              <h5 className="mb-0">{t('Profile.Account', 'Account')}</h5>
            </div>
            <div className="card-body">
              <dl className="row mb-0 fs-sm">
                <dt className="col-6">{t('Profile.UserName', 'User name')}</dt>
                <dd className="col-6 text-end">{profile.userName}</dd>

                <dt className="col-6">{t('Profile.EmailConfirmed', 'Email confirmed')}</dt>
                <dd className="col-6 text-end">
                  {profile.emailConfirmed ? (
                    <span className="badge bg-success">{t('Yes', 'Yes')}</span>
                  ) : (
                    <span className="badge bg-warning">{t('No', 'No')}</span>
                  )}
                </dd>

                <dt className="col-6">{t('Profile.LastLogin', 'Last login')}</dt>
                <dd className="col-6 text-end">{formatDateTime(profile.lastLoginDate)}</dd>

                <dt className="col-6">{t('Profile.LastLoginIp', 'From')}</dt>
                <dd className="col-6 text-end">{profile.lastLoginIpAddress || '—'}</dd>

                <dt className="col-6">{t('Profile.MemberSince', 'Member since')}</dt>
                <dd className="col-6 text-end mb-0">{formatDateTime(profile.createdDate)}</dd>
              </dl>
            </div>
          </div>

          <SecurityCard />
        </div>

        <div className="col-lg-8">
          <div className="card shadow-sm mb-3">
            <div className="card-header bg-transparent">
              <h5 className="mb-0">{t('Profile.Details', 'Details')}</h5>
            </div>
            <div className="card-body">
              <form
                onSubmit={(event) => {
                  event.preventDefault();
                  updateProfile.mutate(details, {
                    onSuccess: () =>
                      setNotice({ tone: 'success', text: t('Profile.Saved', 'Profile saved.') }),
                    onError: (error) =>
                      setNotice({
                        tone: 'danger',
                        text: errorText(error) ?? t('Profile.SaveFailed', 'Could not save.'),
                      }),
                  });
                }}
              >
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="form-label" htmlFor="pf-first">
                      {t('Profile.FirstName', 'First name')}
                    </label>
                    <input
                      id="pf-first"
                      className="form-control"
                      value={details.firstName}
                      onChange={(e) => setDetails({ ...details, firstName: e.target.value })}
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label" htmlFor="pf-last">
                      {t('Profile.LastName', 'Last name')}
                    </label>
                    <input
                      id="pf-last"
                      className="form-control"
                      value={details.lastName}
                      onChange={(e) => setDetails({ ...details, lastName: e.target.value })}
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label" htmlFor="pf-phone">
                      {t('Profile.Phone', 'Phone number')}
                    </label>
                    <input
                      id="pf-phone"
                      className="form-control"
                      value={details.phoneNumber ?? ''}
                      onChange={(e) =>
                        setDetails({ ...details, phoneNumber: e.target.value || null })
                      }
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label" htmlFor="pf-dept">
                      {t('Profile.Department', 'Department')}
                    </label>
                    <input
                      id="pf-dept"
                      className="form-control"
                      value={details.department ?? ''}
                      onChange={(e) =>
                        setDetails({ ...details, department: e.target.value || null })
                      }
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label" htmlFor="pf-job">
                      {t('Profile.JobTitle', 'Job title')}
                    </label>
                    <input
                      id="pf-job"
                      className="form-control"
                      value={details.jobTitle ?? ''}
                      onChange={(e) => setDetails({ ...details, jobTitle: e.target.value || null })}
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label" htmlFor="pf-email">
                      {t('Profile.Email', 'Email')}
                    </label>
                    {/* Read-only: changing an email is an identity operation
                        with a confirmation flow, not a profile edit. The Razor
                        form did not offer it either. */}
                    <input id="pf-email" className="form-control" value={profile.email} disabled />
                  </div>
                </div>

                <button type="submit" className="btn btn-primary" disabled={updateProfile.isPending}>
                  {updateProfile.isPending ? t('Saving', 'Saving…') : t('Save', 'Save')}
                </button>
              </form>
            </div>
          </div>

          <div className="card shadow-sm">
            <div className="card-header bg-transparent">
              <h5 className="mb-0">{t('Profile.Preferences', 'Preferences')}</h5>
            </div>
            <div className="card-body">
              <form
                onSubmit={(event) => {
                  event.preventDefault();
                  updatePreferences.mutate(prefs, {
                    onSuccess: () =>
                      setNotice({
                        tone: 'success',
                        text: t('Profile.PreferencesSaved', 'Preferences saved.'),
                      }),
                    onError: (error) =>
                      setNotice({
                        tone: 'danger',
                        text: errorText(error) ?? t('Profile.SaveFailed', 'Could not save.'),
                      }),
                  });
                }}
              >
                <div className="row">
                  <div className="col-md-4 mb-3">
                    <label className="form-label" htmlFor="pf-lang">
                      {t('Profile.Language', 'Language')}
                    </label>
                    <select
                      id="pf-lang"
                      className="form-select"
                      value={prefs.preferredLanguage}
                      onChange={(e) => setPrefs({ ...prefs, preferredLanguage: e.target.value })}
                    >
                      {Object.entries(availableLanguages).map(([code, name]) => (
                        <option key={code} value={code}>
                          {name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="col-md-4 mb-3">
                    <label className="form-label" htmlFor="pf-tz">
                      {t('Profile.Timezone', 'Timezone')}
                    </label>
                    <select
                      id="pf-tz"
                      className="form-select"
                      value={prefs.preferredTimezone}
                      onChange={(e) => setPrefs({ ...prefs, preferredTimezone: e.target.value })}
                    >
                      {availableTimezones.map((zone) => (
                        <option key={zone} value={zone}>
                          {zone}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="col-md-4 mb-3">
                    <label className="form-label" htmlFor="pf-theme">
                      {t('Profile.Theme', 'Theme')}
                    </label>
                    <select
                      id="pf-theme"
                      className="form-select"
                      value={prefs.preferredTheme}
                      onChange={(e) => setPrefs({ ...prefs, preferredTheme: e.target.value })}
                    >
                      {Object.entries(availableThemes).map(([key, name]) => (
                        <option key={key} value={key}>
                          {name}
                        </option>
                      ))}
                    </select>
                    {/* Stored, not applied. LayoutProvider owns the live theme
                        and persists it to localStorage; this column has always
                        been a saved preference nothing reads back. Wiring the
                        two together is a decision about which one wins, not a
                        detail of this migration. */}
                    <div className="form-text d-flex align-items-start gap-1">
                      <SpriteIcon name="info" size={14} className="sa-icon mt-1 flex-shrink-0" />
                      <span>
                        {t(
                          'Profile.ThemeHint',
                          'Saved to your account. The theme in use is set from the settings panel.',
                        )}
                      </span>
                    </div>
                  </div>
                </div>

                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={updatePreferences.isPending}
                >
                  {updatePreferences.isPending ? t('Saving', 'Saving…') : t('Save', 'Save')}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
